/**
 * Size guide page (sections/size-guide-page.liquid) — interactive blocks.
 *
 * Garment tabs + measurement pills ([data-sg-garments]):
 *   [data-sg-tab]   -> shows its [data-sg-panel] (aria-controls)
 *   [data-sg-pill]  -> shows the [data-sg-slide] with the same index inside its panel
 *
 * All tab/pill images are preloaded when the block comes within 800px of the viewport.
 * Uses event delegation so it also works after theme-editor re-renders.
 */
(function () {
  'use strict';

  function selectTab(tab) {
    var root = tab.closest('[data-sg-garments]');
    if (!root) return;
    root.querySelectorAll('[data-sg-tab]').forEach(function (t) {
      var on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      var panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  }

  function selectPill(pill) {
    var panel = pill.closest('[data-sg-panel]');
    if (!panel) return;
    var index = pill.getAttribute('data-sg-pill');
    panel.querySelectorAll('[data-sg-pill]').forEach(function (p) {
      p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
    });
    panel.querySelectorAll('[data-sg-slide]').forEach(function (s) {
      s.hidden = s.getAttribute('data-sg-slide') !== index;
    });
  }

  // Hidden tab/pill images are lazy, so the browser would only fetch them on click.
  // Switch them to eager (+ decode) once the block nears the viewport so tab/pill changes are instant.
  function preloadImages(root) {
    if (root.__sgPreloaded) return;
    root.__sgPreloaded = true;
    var isV2 = document.documentElement.getAttribute('data-page-size') === 'v2';
    root.querySelectorAll('[data-sg-slide] img, [data-sg-build-view] img').forEach(function (img) {
      // Real builds: only preload the image set for the active version (V1 or V2)
      if (img.classList.contains('sg-build__img--v2') && !isV2) return;
      if (isV2 && img.classList.contains('sg-build__img--v1') && img.closest('.sg-build__view--has-v2')) return;
      img.loading = 'eager';
      if (img.decode) img.decode().catch(function () {});
    });
  }

  var preloadObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          preloadImages(entry.target);
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '800px 0px' })
    : null;

  // If the "tab open on load" garment has no name, open the first tab instead.
  function init(scope) {
    (scope || document).querySelectorAll('[data-sg-garments]').forEach(function (root) {
      var tabs = root.querySelectorAll('[data-sg-tab]');
      // V2: open the tab chosen in "V2: tab open on load"
      var v2Tab = root.getAttribute('data-v2-tab');
      var v2Btn = v2Tab && document.documentElement.getAttribute('data-page-size') === 'v2'
        ? root.querySelector('[data-sg-tab][data-v2-first]') : null;
      if (v2Btn) selectTab(v2Btn);
      else if (tabs.length && !root.querySelector('[data-sg-tab][aria-selected="true"]')) selectTab(tabs[0]);
      if (preloadObserver) preloadObserver.observe(root);
      else preloadImages(root);
    });
    (scope || document).querySelectorAll('[data-sg-builds]').forEach(function (root) {
      if (preloadObserver) preloadObserver.observe(root);
      else preloadImages(root);
    });
  }

  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-sg-tab]');
    if (tab) return selectTab(tab);
    var pill = e.target.closest('[data-sg-pill]');
    if (pill) selectPill(pill);
  });

  // Arrow-key navigation between tabs
  document.addEventListener('keydown', function (e) {
    var tab = e.target.closest && e.target.closest('[data-sg-tab]');
    if (!tab || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return;
    var tabs = Array.prototype.slice.call(tab.closest('[role="tablist"]').querySelectorAll('[data-sg-tab]'))
      .sort(function (a, b) { return a.getBoundingClientRect().left - b.getBoundingClientRect().left; }); // visual order (V2 reorders)
    var next = tabs[(tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
    next.focus();
    selectTab(next);
    e.preventDefault();
  });

  /* ------------------------------------------------------------------
   * Size chart – "What are you shopping for?" ([data-sg-shop])
   * Each category's "Size table" (data-table: one row per line, cells split by |,
   * first line = headings, values in cm) builds a short table (selected columns)
   * + full table (all columns). IN = cm / 2.54.
   * ------------------------------------------------------------------ */
  var norm = function (t) { return String(t || '').toLowerCase().replace(/[^a-z0-9]/g, ''); };

  // Custom table from the category's "Size table" setting
  function customTable(text) {
    var lines = String(text || '').split(/\r?\n/).map(function (l) { return l.trim(); }).filter(Boolean);
    if (lines.length < 2) return null;
    var split = function (l) { return l.split('|').map(function (c) { return c.trim(); }); };
    return { head: split(lines[0]), body: lines.slice(1).map(split) };
  }

  // cm -> inches (1 decimal) for every number in a cell, e.g. "86-91" -> "33.9-35.8"
  function toInches(data) {
    var skip = data.head.map(function (h, i) { return i === 0 || /size/i.test(h); });
    var conv = function (v) {
      return String(v).replace(/\d+(?:\.\d+)?/g, function (n) {
        return String(Math.round(parseFloat(n) / 2.54 * 10) / 10);
      });
    };
    return {
      head: data.head,
      body: data.body.map(function (row) {
        return row.map(function (c, i) { return skip[i] ? c : conv(c); });
      })
    };
  }

  function buildTable(data, cols, highlight) {
    var table = document.createElement('table');
    table.className = 'sg-size-table';
    var thead = table.createTHead().insertRow();
    cols.forEach(function (i) {
      var th = document.createElement('th');
      th.scope = 'col';
      th.textContent = data.head[i] || '';
      thead.appendChild(th);
    });
    var tbody = table.createTBody();
    data.body.forEach(function (row) {
      var tr = tbody.insertRow();
      if (highlight && norm(row[0]) === norm(highlight)) tr.className = 'is-highlight';
      cols.forEach(function (i, n) {
        var cell = document.createElement(n === 0 ? 'th' : 'td');
        if (n === 0) cell.scope = 'row';
        cell.textContent = row[i] == null ? '' : row[i];
        tr.appendChild(cell);
      });
    });
    return table;
  }

  function renderShop(root) {
    var cat = root.querySelector('[data-sg-cat][aria-pressed="true"]');
    var short = root.querySelector('[data-sg-short]');
    var full = root.querySelector('[data-sg-full]');
    var title = root.querySelector('[data-sg-shop-title]');
    if (!cat || !short) return;
    title.textContent = cat.getAttribute('data-name');

    var note = root.querySelector('[data-sg-cat-note]');
    if (note) {
      note.textContent = cat.getAttribute('data-table-note') || '';
      note.hidden = !note.textContent;
    }

    var data = customTable(cat.getAttribute('data-table'));
    if (data && root.__sgUnit === '0') data = toInches(data);
    if (!data) {
      short.innerHTML = '';
      var msg = document.createElement('p');
      msg.className = 'sg-shop__status';
      msg.textContent = 'Size chart not available for ' + cat.getAttribute('data-name') + '.';
      short.appendChild(msg);
      if (full) full.innerHTML = '';
      return;
    }

    var all = data.head.map(function (_, i) { return i; });
    var wanted = (cat.getAttribute('data-columns') || '').split(',').map(norm).filter(Boolean);
    var cols = wanted.length
      ? [0].concat(all.filter(function (i) { return i > 0 && wanted.indexOf(norm(data.head[i])) !== -1; }))
      : all;
    if (cols.length < 2) cols = all;
    var highlight = cat.getAttribute('data-highlight');

    short.innerHTML = '';
    short.appendChild(buildTable(data, cols, highlight));
    if (full) {
      full.innerHTML = '';
      full.appendChild(buildTable(data, all, highlight));
    }
  }

  function initShop(root) {
    if (root.__sgShop) return;
    root.__sgShop = true;
    root.__sgUnit = '1';
    renderShop(root);
  }

  function selectGroup(btn) {
    var root = btn.closest('[data-sg-shop]');
    var group = btn.getAttribute('data-sg-group');
    root.querySelectorAll('[data-sg-group]').forEach(function (b) {
      var on = b === btn;
      b.setAttribute('aria-selected', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
    });
    var first = null;
    root.querySelectorAll('[data-sg-cat]').forEach(function (c) {
      var show = c.getAttribute('data-group') === group;
      c.hidden = !show;
      if (show && !first) first = c;
    });
    if (first) selectCat(first);
  }

  function selectCat(cat) {
    var root = cat.closest('[data-sg-shop]');
    root.querySelectorAll('[data-sg-cat]').forEach(function (c) {
      c.setAttribute('aria-pressed', c === cat ? 'true' : 'false');
    });
    renderShop(root);
  }

  function selectUnit(btn) {
    var root = btn.closest('[data-sg-shop]');
    var value = btn.getAttribute('data-sg-unit');
    root.querySelectorAll('[data-sg-unit]').forEach(function (b) {
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });
    root.__sgUnit = value;
    renderShop(root);
  }

  function toggleAccordion(btn) {
    var open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    var panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (panel) panel.hidden = !open;
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-sg-group], [data-sg-cat], [data-sg-unit], [data-sg-acc]');
    if (!t || !t.closest('[data-sg-shop]')) return;
    if (t.hasAttribute('data-sg-group')) selectGroup(t);
    else if (t.hasAttribute('data-sg-cat')) selectCat(t);
    else if (t.hasAttribute('data-sg-unit')) selectUnit(t);
    else toggleAccordion(t);
  });

  /* ------------------------------------------------------------------
   * Measure steps ([data-sg-steps]) — step bar jump links.
   * Each link targets the first block of a type (id="sg-<type>").
   * Smooth-scrolls to it (CSS scroll-margin-top clears the sticky header)
   * and marks the step whose target was scrolled past as aria-current.
   * ------------------------------------------------------------------ */
  function stepTarget(link) {
    var el = document.getElementById('sg-' + link.getAttribute('data-sg-step-link'));
    return el && el.offsetParent !== null ? el : null; // skip blocks hidden in this version
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-sg-step-link]');
    if (!link) return;
    var target = stepTarget(link);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  var stepTicking = false;
  function updateSteps() {
    stepTicking = false;
    document.querySelectorAll('[data-sg-steps]').forEach(function (bar) {
      var links = bar.querySelectorAll('[data-sg-step-link]');
      var current = links[0];
      var line = window.innerHeight * 0.4;
      links.forEach(function (link) {
        var target = stepTarget(link);
        if (target && target.getBoundingClientRect().top <= line) current = link;
      });
      links.forEach(function (link) {
        if (link === current) link.setAttribute('aria-current', 'step');
        else link.removeAttribute('aria-current');
      });
    });
  }
  window.addEventListener('scroll', function () {
    if (!stepTicking) { stepTicking = true; requestAnimationFrame(updateSteps); }
  }, { passive: true });

  /* ------------------------------------------------------------------
   * Real builds ([data-sg-builds]) — garment tabs + Front/Side/Back views
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-sg-builds-tab]');
    if (tab) {
      var root = tab.closest('[data-sg-builds]');
      root.querySelectorAll('[data-sg-builds-tab]').forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      return;
    }
    var view = e.target.closest('[data-sg-view]');
    if (!view) return;
    var card = view.closest('[data-sg-build]');
    if (!card) return;
    var index = view.getAttribute('data-sg-view');
    card.querySelectorAll('[data-sg-view]').forEach(function (v) {
      v.setAttribute('aria-pressed', v === view ? 'true' : 'false');
    });
    card.querySelectorAll('[data-sg-build-view]').forEach(function (v) {
      v.hidden = v.getAttribute('data-sg-build-view') !== index;
    });
  });

  /* ------------------------------------------------------------------
   * Swipe sliders ([data-sg-slider] track + sibling [data-sg-dots]).
   * Native horizontal scroll + scroll-snap (CSS); JS only builds the dots,
   * scrolls to a slide on dot click and marks the current dot on scroll.
   * Dots are only visible where CSS shows them (V2, mobile).
   * ------------------------------------------------------------------ */
  function initSlider(track) {
    if (track.__sgSlider) return;
    track.__sgSlider = true;
    var dots = track.parentElement.querySelector('[data-sg-dots]');
    if (!dots) return;
    var slides = Array.prototype.filter.call(track.children, function (c) { return c.nodeType === 1; });
    dots.innerHTML = '';
    slides.forEach(function (slide, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'sg-dots__dot';
      b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      b.addEventListener('click', function () {
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || 0), behavior: 'smooth' });
      });
      dots.appendChild(b);
    });
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var start = track.getBoundingClientRect().left + parseFloat(getComputedStyle(track).paddingLeft || 0);
        var best = 0, bestDist = Infinity;
        slides.forEach(function (s, i) {
          var d = Math.abs(s.getBoundingClientRect().left - start);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        // at the very end, mark the last slide
        if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) best = slides.length - 1;
        Array.prototype.forEach.call(dots.children, function (d, i) {
          d.setAttribute('aria-current', i === best ? 'true' : 'false');
        });
      });
    }, { passive: true });
  }

  function initShops(scope) {
    (scope || document).querySelectorAll('[data-sg-slider]').forEach(initSlider);
    (scope || document).querySelectorAll('[data-sg-shop]').forEach(initShop);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initShops(); });
  } else {
    initShops();
  }
  document.addEventListener('shopify:section:load', function (e) { initShops(e.target); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  // A/B tool may switch the version after load (page-size:change from ab-page-size-snippet)
  document.addEventListener('page-size:change', function (e) {
    if (!e.detail || e.detail.variant !== 'v2') return;
    document.querySelectorAll('[data-sg-garments][data-v2-tab] [data-sg-tab][data-v2-first]').forEach(selectTab);
  });
})();
